import { Controller } from "stimulus";

class AmountController extends Controller {
    static targets = ["optionButton", "otherOptionButton", "otherSection", "otherInput", "otherSectionError", "otherDiscountInfo"];

    get paymentAmount() {
        return this.getNumericData("payment-amount");
    }

    set paymentAmount(value) {
        this.data.set("payment-amount", value.toString());
        this.publishPaymentAmountUpdatedEvent(value);
    }

    get paymentTotalAmount() {
        return this.getNumericData("payment-total-amount");
    }

    set paymentTotalAmount(value) {
        this.data.set("payment-total-amount", value.toString());
        this.publishPaymentTotalAmountUpdatedEvent(value);
    }

    get voucherValue() {
        return this.getNumericData("voucher-value");
    }

    set voucherValue(value) {
        this.data.set("voucher-amount", value.toString());
        this.publishVoucherValueUpdatedEvent(value);
    }

    get processingFeeAmount() {
        return this.getNumericData("processing-fee-amount");
    }

    set processingFeeAmount(value) {
        this.data.set("processing-fee-amount", value.toString());
        this.publishProcessingFeeUpdatedEvent(value);
    }

    get discountType() {
        return this.data.get("discount-type");
    }


    get discountEnabled() {
        return this.data.get("discount-enabled") === 'true';
    }

    get discount() {
        return parseInt(this.data.get("discount"));
    }

    get processingFeeValue() {
        return this.getNumericData("processing-fee-percentage");
    }

    get processingFeeEnabled() {
        return this.data.get("processing-fee-enabled") === 'true';
    }

    get otherSectionVisible() {
        return !this.otherSectionTarget.classList.contains("hidden");
    }

    set otherSectionVisible(visible) {
        this.otherSectionTarget.classList.toggle("hidden", !visible);
        this.otherSectionErrorTarget.classList.toggle("hidden", !visible);
    }

    get otherInputValue() {
        return this.otherInputTarget.value;
    }

    set otherInputValue(value) {
        this.otherInputTarget.value = value;
    }

    get discountSpanTemplate() {
        return this.data.get("discount-span-template");
    }

    optionSelected(event) {
        this.otherSectionVisible = false;
        this.paymentAmount = parseFloat(event.target.getAttribute("data-payment-amount"));
        this.paymentTotalAmount = parseFloat(event.target.getAttribute("data-payment-total-amount"));
        this.voucherValue = parseFloat(event.target.getAttribute("data-voucher-value"));
        this.processingFeeAmount = parseFloat(event.target.getAttribute("data-processing-fee-amount"));
        let displayValue = parseInt(event.target.getAttribute("data-display-amount"));
        this.updateFixedOptionSelection(displayValue);
    }

    otherOptionSelected() {
        this.otherSectionVisible = true;
        this.value = 0;
        this.otherInputValue = "";
        this.validateOtherFieldAmount();
        this.otherInputTarget.focus();
        this.updateFixedOptionSelection(0);
        this.toggleOptionSelection(this.otherOptionButtonTarget, true);
    }

    otherInputUpdated() {
        if (this.validateOtherFieldAmount()) {
            const selectedAmount = parseInt(this.otherInputValue);
            this.paymentAmount = this.calculatePaymentAmount(selectedAmount);
            this.voucherValue = this.calculateVoucherAmount(selectedAmount);
            this.processingFeeAmount = this.calculateProcessingFeeAmount(selectedAmount);
            this.paymentTotalAmount = this.paymentAmount + this.processingFeeAmount;
            this.otherDiscountInfoTarget.innerHTML = this.discountSpanTemplate.replace('AMOUNT', this.discountAmountInfo(selectedAmount));
            this.otherDiscountInfoTarget.classList.toggle('hidden', !this.discountEnabled);
        }
    }

    preventInvalidCharacters(event) {
        let maxLengthExceeded = this.value.toString().length >= 16;
        let correctKey = event.metaKey || event.which <= 0 || event.which == 8 || /[0-9]/.test(String.fromCharCode(event.which)); // numbers
        if (!correctKey || maxLengthExceeded) {
            event.preventDefault();
        }
    }

    publishPaymentAmountUpdatedEvent(value) {
        const event = document.createEvent("CustomEvent");
        event.initCustomEvent("payment-amount-updated", true, true, null);
        event.paymentAmount = value;
        this.element.dispatchEvent(event);
    }

    publishVoucherValueUpdatedEvent(value) {
        const event = document.createEvent("CustomEvent");
        event.initCustomEvent("voucher-value-updated", true, true, null);
        event.voucherValue = value;
        this.element.dispatchEvent(event);
    }

    publishProcessingFeeUpdatedEvent(value) {
        const event = document.createEvent("CustomEvent");
        event.initCustomEvent("processing-fee-updated", true, true, null);
        event.processingFeeAmount = value;
        this.element.dispatchEvent(event);
    }

    publishPaymentTotalAmountUpdatedEvent(value) {
        const event = document.createEvent("CustomEvent");
        event.initCustomEvent("payment-total-amount-updated", true, true, null);
        event.paymentTotalAmount = value;
        this.element.dispatchEvent(event);
    }

    validateOtherFieldAmount() {
        if (this.otherInputValue === "") {
            this.otherSectionErrorTarget.innerHTML = this.data.get('required-error');
            this.otherSectionErrorTarget.classList.remove('hidden');
            return false;
        } else if (this.invalidOtherAmountValue()) {
            this.otherSectionErrorTarget.innerHTML = this.data.get('spend-error')
            this.otherSectionErrorTarget.classList.remove('hidden');
            return true;
        } else {
            this.otherSectionErrorTarget.classList.add('hidden');
            return true;
        }
    }

    invalidOtherAmountValue() {
        let value = parseInt(this.otherInputValue);
        let paymentAmount = this.calculatePaymentAmount(value);
        let processingFeeAmount = this.calculateProcessingFeeAmount(value);
        let totalAmount = paymentAmount + processingFeeAmount;
        let minSpend = parseFloat(this.data.get('min-spend-amount'));
        let maxSpend = parseFloat(this.data.get('max-spend-amount'));
        return totalAmount < minSpend || totalAmount > maxSpend;
    }


    updateFixedOptionSelection(displayValue) {
        this.optionButtonTargets.forEach((optionButton) => {
            let optionValue = optionButton.getAttribute("data-display-amount");
            let selected = parseInt(optionValue) === displayValue;
            this.toggleOptionSelection(optionButton, selected);
        });
        this.toggleOptionSelection(this.otherOptionButtonTarget, false);
    }


    updateOtherOptionForInitialState() {
        let otherOptionButton = this.otherOptionButtonTarget;
        if (this.value === 0) { // no option nor value is set yet
            this.toggleOptionSelection(otherOptionButton, false);
        } else { // connecting with a other value set
            this.otherSectionVisible = true;
            this.otherInputValue = this.paymentAmount;
            this.toggleOptionSelection(otherOptionButton, true);
        }
    }

    toggleOptionSelection(optionButton, selected) {
        optionButton.checked = selected;
    }

    calculateVoucherAmount(value) {
        if (this.discountType === "value_add") {
            return value + this.discountAmount(value);

        } else {
            return value;
        }
    }

    calculatePaymentAmount(value) {
        if (this.discountType === 'price_reduction') {
            return value - this.discountAmount(value);
        } else {
            return value;
        }
    }

    calculateProcessingFeeAmount(value) {
        return this.calculatePaymentAmount(value) * (this.processingFeeEnabled ? this.processingFeeValue : 0)
    }

    discountAmount(value) {
        if (!this.discountEnabled)
            return 0;
        return value * (this.discount/100.0);
    }

    discountAmountInfo(value) {
        if (this.discountType === 'price_reduction') {
            return (value - this.discountAmount(value)).toFixed(2);
        } else {
            return this.discountAmount(value).toFixed(2);
        }
    }

    // Helper method for defensive numeric data parsing
    getNumericData(key) {
        const value = parseFloat(this.data.get(key));
        return isNaN(value) ? 0 : value;
    }

}

export default AmountController;
